import {Col, React, Row, Table, Select, StylesConfig} from "../../../../common/web_common/components/eevi_react_exports";
import { useEffect, useRef } from "react";
import {EventDetailsData} from "./event_components";
import {strEnum} from "../../../../common/web_common/components/eevi_transform";
import {EeviCoreContext} from "../../../../common/web_common/core/components/eevi_core_context";
import { Carer, ZoneLocation } from "../../../../common/web_common/core/eevi_core_data";

const TAKEN_THRESHOLD = 20*60*1000 //ms
const OPEN_THRESHOLD = 5*60*1000 //ms
export const QUERY_WINDOW = 12*60*60*1000; //ms <- 12 hours

const _durationFormat = strEnum(
    "MINUTE_ONLY",
    "HH:MM",
    "MM:SS",
    "HH:MM:SS"
)
type _durationFormat = Omit<keyof typeof _durationFormat, "[Symbol.iterator]">;

enum EventStatus{
    DANGER,
    WARNING,
    SAFE,
    UNKNOWN
}

interface SmartAnnunciatorSetting {
    carer_availability_message: boolean;
    carer_availability_alarm: boolean;
}

export interface CarerAvailabilityData {
    careGroupCode: string;
    careGroupName: string;
    config: SmartAnnunciatorSetting;
    totalAvailableCarers: number;
    availableCarers: Carer[];
}

export class RealtimeEventCompute {
    protected get base(): EventDetailsData {
        return this as any;
    }

    get responderFullName(): string | undefined{
        if (this.base.eventType === "ASK_ASSISTANCE") {
            return this.base.assistantFullName;
        } else if (this.base.eventType === "RAISE_EMERGENCY") {
            return this.base.emergencyFullName;
        } else {
            return this.base.carerFullName;
        }
    }

    get elapsedTime(): number {
        const now = new Date()
        return (now.valueOf() - Date.parse(this.base.triggeredTime))
    }

    get status(): EventStatus {
        return (this.isCompleted || this.isPresented) ? EventStatus.SAFE
                    : this.isTaken ? EventStatus.WARNING : EventStatus.DANGER
    }

    get active(): boolean {
        return !this.isCompleted // open, taken, presence
    }

    get isTechSupport(): boolean {
        return this.base.eventType === "DEVICE_FAULT" || this.base.eventType === "SYSTEM_FAULT"
    }

    // Event States
    get isOpen(): boolean {
        return this.base.eventState === "OPEN"
    }

    get isTaken(): boolean {
        return this.base.eventState.includes("TAKEN") || Boolean(this.responderFullName)
    }

    get isPresented(): boolean { //Only mark as presented if previously taken to avoid false presence
        return Boolean(this.base.arriveTime)
    }

    get isCompleted(): boolean {
        return this.base.eventState === "COMPLETED"
    }

    get priority(): number {
        if (this.base.eventType === "STAFF_DURESS" || this.base.eventType === "EMERGENCY" || this.base.eventType === "RAISE_EMERGENCY") {
            return 1  // P1
        } else {
            return 100 // Open events are alarms
        }
    }

    // Conditional format
    get isOverThreshold(): boolean {
        if (this.isCompleted || this.isPresented){
            return false
        } else if (this.isTaken) {
            return this.elapsedTime >= TAKEN_THRESHOLD
        } else {
            return this.elapsedTime >= OPEN_THRESHOLD
        }
    }

    get withinQueryWindow(): boolean {
        // Ignore any event outside of query window
        return this.elapsedTime <= QUERY_WINDOW
    }
}

export type RealtimeEvent = RealtimeEventCompute & EventDetailsData
function toTimeString(duration: number, format: _durationFormat = "MINUTE_ONLY"): string{
    function _prefix_w_zero(arr: number[]){
        return arr.map(i => {
            return i < 10 ? `0${i}` : `${i}`
        })
    }

    if (duration) {
        const hour = Math.floor(duration/(60*60*1000))
        let min = Math.floor((duration % (60*60*1000)) / (60*1000))
        const sec = Math.floor(duration % (60*1000) / 1000)
        switch (format){
            case "HH:MM:SS":
                return _prefix_w_zero([hour, min, sec]).join(':')

            case "MINUTE_ONLY":
                min = Math.floor(duration/(60*1000))
                return `${min} ` + (min <= 1 ? "min": "mins")

            case "HH:MM":
                return _prefix_w_zero([hour, min]).join(':')

            case "MM:SS":
                min = Math.floor(duration/(60*1000))
                return _prefix_w_zero([min, sec]).join(':')

            default: // Same as "HH:MM:SS"
                return _prefix_w_zero([hour, min, sec]).join(':')
        }
    }else{
        return ''
    }

}

export function playTone(
    audio: HTMLMediaElement,
    error_handler: any = ()=>{},
    callback: any = ()=>{},
    playStartedWithoutPromise: any = ()=>{}
){
    if (audio) {
        try {
            const playPromise = audio.play()
            if (playPromise !== undefined) {
                playPromise.then(callback).catch(error_handler)
            } else {
                console.warn("Audio play() did not return a promise; waiting for a playback event.")
                const cleanup = () => {
                    audio.removeEventListener("playing", onPlaying)
                    audio.removeEventListener("error", onError)
                }
                const onPlaying = () => {
                    cleanup()
                    callback()
                }
                const onError = (error: Event) => {
                    cleanup()
                    error_handler(error)
                }
                audio.addEventListener("playing", onPlaying)
                audio.addEventListener("error", onError)
                playStartedWithoutPromise()
            }
        } catch (error) {
            error_handler(error)
        }
    } else {
        console.log("No audio file provided")
    }
}

function AlarmRow (
        props:{
            event: RealtimeEvent,
            width:number,
            height:number,
            hideResidentDetails:boolean,
        }
): JSX.Element {
    const e = props.event
    const resident = e.residentFullName
    const eventType = e.eventTitle
    const takenCarer = e.responderFullName || (e.isOverThreshold ? "Not taken" : "")
    const presenceCarer = e.arrivedCarer || ""
    const activeTime = toTimeString(e.elapsedTime)
    const hideResidentDetails = props.hideResidentDetails
    const taken_icon = e.isTaken ? 'taken-icon' : e.isOverThreshold ? 'danger-icon' : ''
    const presence_icon = e.isPresented ? 'presence-icon' : (e.isTaken && e.isOverThreshold) ? 'warning-icon' : ''
    let deviceName = ""
    let lastSeenLocation = null
    let priorLocation = null
    let location = ""
    let carerName = null
    const background = EventStatus[e.status].toLowerCase()
    const text_format = e.isOverThreshold ? 'alert' : ''  // highlight text with red color
    const long_text_format = (t:string, maxLength:number) => {
        return (t && t.length > maxLength) ? "flex-start" : "center"
    } // re-align string t if it is too long

    let resident_td = <td style={{alignItems:`${long_text_format(resident, 60)}`}} className="flex-3">{resident}</td>

    // display more information in locations for Elopement event
    if(e.eventType === "ELOPEMENT"){
        carerName = ""
        deviceName = e.extraData?.deviceWearerName ?? "Unknown"
        lastSeenLocation = e.currentLocation?.geofenceName ?? "Unknown"
        priorLocation = e.previousLocation?.geofenceName ?? "Unknown"
    }

    // display more information in locations for Staff duress event
    if(e.eventType === "STAFF_DURESS"){
        location = ""
        carerName = e.requestedCarerName ?? "Unknown Carer"
        lastSeenLocation = e.currentLocation?.geofenceName ?? "Unknown"
        priorLocation = e.previousLocation?.geofenceName ?? "Unknown"
    }
    else{
        location = e.location
    }

    return <tr className={`d-flex`} style={{height: `auto`, marginBottom: '1vh'}}>
        {/* Flag indicator */}
        <td className={`${background}-background flex-1`} />

        <td
            style={{alignItems:`${long_text_format(location, 60)}`}}
            className="flex-3"
        >
            <span>
                {deviceName? <> {deviceName} <br /> </> : null}
                {location ? <> {location} <br /> </> : null}
                {carerName ? carerName: null}
                <span style={{fontSize:'1.5vh'}}>
                    {lastSeenLocation?<> Last Seen:  {lastSeenLocation} <br /> </> : null}
                    {priorLocation? <> Prior Location:  {priorLocation} </> : null}
                </span>
            </span>
        </td>

        {!hideResidentDetails ? resident_td : null }

        <td className="flex-2">{eventType}</td>

        {/* Taken icon */}
        <td className={`${taken_icon} flex-1`}/>

        {/* takenCarer is a bit different from the rest of the text,
         as it includes the format alert-text along with phrase "Not taken" when it's over the threshold*/}
        <td
            style={{alignItems:`${long_text_format(takenCarer, 45)}`}}
            className={`${e.isTaken ? '' : text_format}-text flex-2`}
        >{takenCarer}</td>

        {/* Presence icon */}
        {presenceCarer !== "" ? (
        <td className={`${presence_icon} flex-1`}/>
        ) : null}
        {presenceCarer !== "" ? (
            <td
            style={{alignItems:`${long_text_format(presenceCarer, 45)}`}}
            className="flex-2">
                {presenceCarer}
            </td>
        ) : <td
        style={{alignItems:`${long_text_format(presenceCarer, 60)}`}}
        className={`${presence_icon} flex-3`}/>}
        <td className={`${text_format}-text flex-2`}>{activeTime}</td>
    </tr>
}

function Pagination(props: {currentPage: number, totalPages: number, onPageChange: CallableFunction}): JSX.Element{
    React.useEffect(() => {
        const timer = setInterval(
            (max: number)=>{
                console.log("Page Changed")
                props.onPageChange((p:number) => {
                    return (p + 1) % max
                })
            },
            10000,
            props.totalPages + 1
        )

        // Make sure that currentPage is less than totalPage
        // Sometimes, what happens is a list of event could change from
        // 6->7 (totalPages: 2->1), for a brief moment before the timer timed out,
        // the current page is has not been refreshed yet -> no items in it
        // empty HTML tag got displayed
        if (props.currentPage > props.totalPages) {
            console.log("Page Reset")
            props.onPageChange(0)
        }

        return () => clearInterval(timer)
    }, [props.totalPages])

    return <tr>
        <td className="table-footer">
            {`Page ${props.currentPage + 1} / ${props.totalPages + 1}`}
        </td>
    </tr>
}

function BlankRow(props:{width: number, height: number}): JSX.Element{
    return <tr style={{background: "transparent", width: `${props.width}vw`, height: `${props.height-1}vh`}}/>
}

function NoActiveEventsMessage(props:{width: number, height: number}): JSX.Element{
    return <tr style={{background: "transparent",width: `${props.width}vw`, height: `${props.height-1}vh`}}>
        <td className="no-active-events">There are no active Events</td>
    </tr>
}

function CarerMessage(
        props: {
            key: string,
            payload: CarerAvailabilityData,
            width: number,
        }
): [JSX.Element, number] {
    const style = {marginBottom: '1vh'}
    let message = `${props.payload.careGroupName}: `
    let carerStatus = "safe"
    let message_height = 0

    if (props.payload.totalAvailableCarers == 0){
        message += "No carer logged in."
        carerStatus = "danger"
        // The carer alarm sound is driven from the carer poll in alarms_form,
        // not from rendering, so we only render the visual message here.
    } else if (props.payload.totalAvailableCarers == 1) {
        message += `1 carer logged in: ${props.payload.availableCarers[0].carerName}.`
        carerStatus = "warning"
    } else {
        const fullNames = props.payload.availableCarers.map((c) => c.carerName).sort()
        message += `${props.payload.totalAvailableCarers} carers logged in: `
        message += `${fullNames.slice(0,-1).join(", ")}, and ${fullNames.slice(-1)}.`
    }

    // calculate the height of this message, and then remove it from the DOM immediately
    const text_box_sample = document.createElement('div')
    text_box_sample.className = 'hidden'
    text_box_sample.style.fontSize = '2vh'
    text_box_sample.style.lineHeight = '2.4vh'
    text_box_sample.style.display = 'flex'
    text_box_sample.style.width = `${90/100 * 90}vw` // we are actually rendering td, which is 90% of tr
    text_box_sample.style.padding = '10.5px' //10.5px default padding for bootstrap table
    text_box_sample.innerText = message

    document.body.appendChild(text_box_sample)
    message_height = text_box_sample.scrollHeight + 4; // top and bottom border 2px
    // Remove it from table immediately, do not render
    document.body.removeChild(text_box_sample)

    return [
        <tr key={props.key} style={style}>
            <td className={`${carerStatus}-background flex-1`} />
            <td style={{justifyContent: "start", flex: 11}}>{message}</td>
        </tr>,
        message_height
    ]
}

export function AlarmTable (props: {zoneLocations: ZoneLocation[], selectedZones: string[], activeEvents: RealtimeEvent[], availableCarers: Map<string,CarerAvailabilityData>, hideResidentDetails:boolean }): JSX.Element{

    // Percentage with respect to viewport - to ensure everything fit in one window,
    // to be refactored once we decided to separate availableCarers and activeEvents
    const [maxWidth, maxHeight] = [90, 80]  //vw, vh
    const eventRowHeight = 11 //vh
    const carerRows: JSX.Element[] = []
    let carerTableHeight = 0 //vh

    //let hideResidentDetails:boolean = true;

    props.availableCarers.forEach(
        (v) => {
            if (v.config.carer_availability_message) {
                const [message_row, height] = CarerMessage({
                    key:v.careGroupCode,
                    payload:v,
                    width:maxWidth,
                })

                carerRows.push(message_row)
                carerTableHeight += (height*100/window.innerHeight + 1) //1vh margin bottom
            }
        }
    )

    const carerTable = <tbody style={{paddingTop: "1vh", borderTop: "2px solid #DCDCD7"}}>
                {carerRows}
        </tbody>
    carerTableHeight += 1 //padding top 1vh

    const eventTableHeight = maxHeight - Math.ceil(carerTableHeight)
    let eventsPerPage = Math.floor(eventTableHeight/eventRowHeight)

    const [currentPage, setCurrentPage] = React.useState(0)
    const totalPages = Math.max(Math.floor((props.activeEvents.length - 1) / eventsPerPage), 0)

    const startIndex = currentPage * eventsPerPage
    const endIndex = startIndex + eventsPerPage
    // Show all events if no zone is selected
    const eventsFilteredByZone = props.selectedZones.length ?
        props.activeEvents.filter(
            (e) => e.broadcast || props.zoneLocations.filter(zone => props.selectedZones.includes(zone.zoneCode)).some(zone =>zone.locationCodes.includes(e.location))
        ) : props.activeEvents

    const displayedEvents = eventsFilteredByZone.slice(startIndex, endIndex)

    const eventTable: JSX.Element[] = displayedEvents.map(
        (e) => {
            return <AlarmRow
                key={e.eventId}
                event={e}
                width={maxWidth}
                height={eventRowHeight}
                hideResidentDetails={props.hideResidentDetails}
            />
        }
    );

    const emptyAreaHeight = eventTableHeight - eventTable.length * eventRowHeight
    if (emptyAreaHeight){
        if (eventTable.length){
            eventTable.push(<BlankRow key={"blank"} width={maxWidth} height={emptyAreaHeight}/>)
        } else {
            eventTable.push(
                <NoActiveEventsMessage key={"no-event-message"} width={maxWidth} height={emptyAreaHeight}/>
            )
        }
    }

    const table = <Table id={"alarm-table"} className="d-flex flex-column align-items-center">
        <thead>
            <tr>
                <th className="flex-1"/>
                <th className="flex-3">Location</th>
                {!props.hideResidentDetails ? <th className="flex-3">Resident</th> : null }
                <th className="flex-2">Event</th>
                <th className="flex-1"/>
                <th className="flex-2">Taken</th>
                <th className="flex-1"/>
                <th className="flex-2">Presence</th>
                <th className="flex-2">Active Time</th>
            </tr>
        </thead>
        <tbody>
            {eventTable}
        </tbody>
        <tfoot>
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
            />
        </tfoot>
        {carerRows.length ? carerTable: null}
    </Table>

    return <div>{table}</div>;
}

export function useRestoreFocus() {
    const lastFocusedRef = useRef<HTMLElement | null>(null);
    const lastFocusedIdRef = useRef<string | null>(null);

    useEffect(() => {
        const handleFocusIn = (e: FocusEvent) => {
            const target = e.target as HTMLElement;
            lastFocusedRef.current = target;
            lastFocusedIdRef.current = target.id || null;
        };
        const handleFocusOut = () => {
            setTimeout(() => {
                if (document.activeElement === document.body) {
                    //though we don't have unmount operations, keep it for good.
                    const candidate = lastFocusedRef.current && lastFocusedRef.current.isConnected
                        ? lastFocusedRef.current
                        : (lastFocusedIdRef.current ? document.getElementById(lastFocusedIdRef.current) : null);
                    candidate?.focus();
                }
            }, 0);
        };

        document.addEventListener("focusin", handleFocusIn);
        document.addEventListener("focusout", handleFocusOut);

        return () => {
            document.removeEventListener("focusin", handleFocusIn);
            document.removeEventListener("focusout", handleFocusOut);
        };
    }, []);
}

const HeaderSelectStyles: StylesConfig = {
  valueContainer: (base) => ({
    ...base,
    display: 'flex',
    flexWrap: 'nowrap',
    overflow: 'hidden',
  }),

  multiValue: (base) => ({
    ...base,
    maxWidth: '100%',
  }),

  multiValueLabel: (base) => ({
    ...base,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    }),
  option: (base, state) => {
    const focusedBg = '#0a58ca';
    const selectedBg = '#e7f1ff';
    return {
      ...base,
      backgroundColor: state.isFocused ? focusedBg : state.isSelected ? selectedBg : base.backgroundColor,
      color: state.isFocused ? '#fff' : state.isSelected ? '#0a58ca' : base.color,
      fontWeight: state.isFocused ? 700 : base.fontWeight,
    };
  },
  menuPortal: (base: any) => ({ ...base, zIndex: 9999,  })
}
const headerSelectKeyDown = (e: React.KeyboardEvent) => {
    // Treat Back/BrowserBack like Escape so it just closes the dropdown
    if (['Backspace', 'Back', 'BrowserBack', 'GoBack'].includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        (e.target as HTMLElement)?.blur();
    }
}
export const AlarmHeader = React.memo(function AlarmHeader(props: {
    now: Date, lastUpdate: Date, selectedZones: string[],
    setSelectedZones: React.Dispatch<React.SetStateAction<string[]>>,
    zoneLocations: ZoneLocation[],
    soundAutoplayBlocked: boolean
}): JSX.Element{
    useRestoreFocus();
    const zoneOptions = React.useMemo(
        () => props.zoneLocations.map(zone => ({
          value: zone.zoneCode,
          label: zone.zoneName ?? zone.zoneCode,
        })),
        [props.zoneLocations]
      );

    const menuPortalTarget = React.useMemo(() => {
        return typeof document !== "undefined" ? document.body : undefined
    }, [])
    const {model} = React.useContext(EeviCoreContext)
    let title = ''
    if (model !== undefined) {
        if (typeof model.careGroups !== "undefined" && typeof model.careGroups !== "string") {
            title = model.careGroups.length == 1 ? model.careGroups[0].careGroupName : "Smart Annunciator"
        }
    }
    return <div className="sticky-top">
         <div className="header-alarms">
            <Row className="header-row">
                <Col md="2" className="header-logo"/>
                <Col md="3">
                    <div className="header-title">
                    <h2>{`${title}`}</h2>
                    <h4>{`${props.now.toLocaleDateString(undefined, {year: 'numeric', month: 'short', day: 'numeric' })}`}</h4>
                    </div>
                </Col>
                <Col md="4" className="header-zone-select">
                    {props.soundAutoplayBlocked ? (
                        <div className="sound-autoplay-warning">Need user interaction to play sounds</div>
                    ) : null}
                    <Select
                        autoFocus
                        isMulti
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        placeholder="Select Zone"
                        value={zoneOptions.filter(option => props.selectedZones.includes(option.value))}
                        onChange={(selected) => {
                            const selections = Array.isArray(selected) ? selected.map(option => option.value) : [];
                            props.setSelectedZones(selections);
                        }}
                        menuPortalTarget={menuPortalTarget}
                        styles={HeaderSelectStyles}
                        instanceId="alarm-zone-select"
                        options={zoneOptions}
                        onKeyDown={headerSelectKeyDown}
                    />
                </Col>
                <Col md="3">
                    <div className="header-time">
                    <h2>
                        {`${props.now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second:'2-digit' })}`}
                    </h2>
                    <h4>{`Last data refresh: ${props.lastUpdate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}</h4>
                    </div>
                </Col>
            </Row>
        </div>
    </div>
})
